import { Component, DestroyRef, signal, ViewChild, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { ErrorParser } from '@app/classes';
import { BadgeOptions } from '@app/interfaces';
import { T4Summary } from '@app/models/payroll/t4-summary.model';
import { T4IssueForm } from '@app/modules/forms/payroll/components';
import { AddAmendedTaxForm } from '@app/modules/forms/payroll/components/add-amended-tax/add-amended-tax.form';
import { PayrollModuleMenuService } from '@app/modules/payroll/services/payroll-module-menu.service';
import { AuthService, FileHelperService, NotifyService } from '@app/services';
import { PayrollResolver } from '@payroll/payroll.resolver';
import { T4IndexTable, T4NotificationsService, T4Service } from '@payroll/services';
import partition from 'lodash-es/partition';
import size from 'lodash-es/size';
import { filter } from 'rxjs/operators';

@Component({
    selector: 'app-t4-summary-show',
    templateUrl: './t4-summary-show.template.html',
    styleUrls: ['./t4-summary-show.style.scss'],
})
export class T4SummaryShow {
    @ViewChild(AddAmendedTaxForm) addAmendedTaxForm?: AddAmendedTaxForm;

    currentlyOpenDialog?:
        | 'T4_ISSUE_CONFIRMATION'
        | 'SELECT_AMENDED_T4'
        | 'BULK_EMAIL_CONFIRMATION'
        | 'T4_DELETE_CONFIRMATION';

    t4Summary: T4Summary;
    isLoading = true;
    employeeT4Table: T4IndexTable;

    headerBadgeOptions?: BadgeOptions;

    backRoute: string;

    definedBenefitsNotificationEmployeesCount = 0;
    definedContributionAndProfitSharingNotificationEmployeesCount = 0;

    employeesInAmendment: WritableSignal<{ [employeeId: string]: { T4?: boolean; RL1?: boolean } }> = signal({});

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private resolver: PayrollResolver,
        private auth: AuthService,
        private notify: NotifyService,
        private fileHelper: FileHelperService,
        private t4Service: T4Service,
        private t4Notifications: T4NotificationsService,
        public payrollModuleMenuService: PayrollModuleMenuService,
        private destroyRef: DestroyRef
    ) {
        this.t4Summary = this.route.snapshot.data.models.get('t4Summary');

        this.employeeT4Table = new T4IndexTable(this.auth, this.t4Summary.year, this.t4Summary.id);

        if (this.t4Summary.isAmendment) {
            this.subscribeToEmployeeTaxFormsTableChanges();
        }

        this.backRoute = `/payroll/records/tax-forms/${this.t4Summary.year.toString()}`;

        this.updateHeaderBadge();

        this.t4Notifications.getSummaryNotifications(this.t4Summary.id).subscribe((notifications) => {
            const filteredEmployees = notifications.employees.filter((employee) => employee.boxes.includes('50'));
            const [hasBox52, doesntHaveBox52] = partition(filteredEmployees, (employee) =>
                employee.boxes.includes('52')
            ).map(size);

            this.definedBenefitsNotificationEmployeesCount = hasBox52;
            this.definedContributionAndProfitSharingNotificationEmployeesCount = doesntHaveBox52;
            this.isLoading = false;
        });
    }

    /**
     * When employees are added/removed from the amendment, ensure they're filtered from the employee auto-complete
     */
    subscribeToEmployeeTaxFormsTableChanges(): void {
        this.employeeT4Table.onLoading
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                filter((isLoading) => !isLoading)
            )
            .subscribe(() =>
                this.employeeT4Table.paginator.values.forEach((taxForm) =>
                    this.employeesInAmendment.set({
                        ...this.employeesInAmendment(),
                        [taxForm.employee.id.toString()]: { T4: true },
                    })
                )
            );
    }

    closeDialog(): void {
        this.currentlyOpenDialog = undefined;
    }

    async addAmendedTax(form?: AddAmendedTaxForm): Promise<void> {
        const prEmployeeId = form?.addEmployee()?.prEmployeeId;
        if (!prEmployeeId) {
            return;
        }

        form.submit();

        if (!form.valid) {
            return;
        }

        this.closeDialog();

        try {
            this.isLoading = true;
            await this.t4Service.addAmendedT4(this.t4Summary.year, prEmployeeId);
            this.notify.success('payroll.taxReturns.amendment.T4.created');
            this.employeeT4Table.reload();
            this.isLoading = false;
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }
    }

    async finalize(form: T4IssueForm): Promise<void> {
        form.submit();

        if (!form.valid || !form.hasConfirmed()) {
            return;
        }

        this.closeDialog();

        try {
            this.isLoading = true;
            this.t4Service.issue(this.t4Summary.id, true);
            this.notify.success('payroll.taxForms.t4sFinalized');

            const models = await this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot);

            this.t4Summary = <T4Summary>models.get('t4Summary');

            this.updateHeaderBadge();

            this.employeeT4Table.reload();

            this.isLoading = false;
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as Record<string, unknown>));
            this.isLoading = false;
        }
    }

    async amend(): Promise<void> {
        this.isLoading = true;
        try {
            const t4Summary = new T4Summary();
            t4Summary.year = this.t4Summary.year;

            const amendedT4Summary = await t4Summary.param('company', this.auth.company.id).save();

            this.router.navigate(['/payroll/records/tax-forms/t4-summary', amendedT4Summary.id.toString()]);
        } catch (e) {
            this.notify.error(e);
            this.isLoading = false;
        }
    }

    async bulkEmail(): Promise<void> {
        this.isLoading = true;
        try {
            await this.t4Service.sendBulkEmails(this.t4Summary);
            this.notify.success('payroll.taxForms.t4sEmailed');
            this.isLoading = false;
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as Record<string, unknown>));
            this.isLoading = false;
        }
    }

    async delete(): Promise<void> {
        this.isLoading = true;
        try {
            await this.t4Summary.param('company', this.auth.company.id).delete();
            this.notify.success('payroll.taxForms.T4.deleted');

            this.router.navigate(['/payroll/records/tax-forms', this.t4Summary.year]);
        } catch (e) {
            this.notify.error(ErrorParser.parse(e as Record<string, unknown>));
            this.isLoading = false;
        }
    }

    async download(): Promise<void> {
        this.isLoading = true;

        const year = this.t4Summary.year;
        const t4SummaryPath = this.downloadPathForT4Summary;

        this.fileHelper.savePayrollFile(
            t4SummaryPath,
            `T619_${year.toString()}`,
            'zip',
            {
                year,
            },
            (e) => {
                if (e) {
                    this.notify.error(ErrorParser.parse(e));
                }

                this.isLoading = false;
            }
        );
    }

    async downloadT619(): Promise<void> {
        this.isLoading = true;

        const year = this.t4Summary.year;
        const downloadT619Path = this.downloadPathForT619;

        this.fileHelper.savePayrollFile(
            downloadT619Path,
            `T619_${year.toString()}`,
            'xml',
            {
                year,
            },
            (e) => {
                if (e) {
                    this.notify.error(ErrorParser.parse(e));
                }

                this.isLoading = false;
            }
        );
    }

    private updateHeaderBadge(): void {
        this.headerBadgeOptions = {
            type: this.t4Summary.isSubmitted ? 'success' : 'informative',
            label: this.t4Summary.isSubmitted ? 'payroll.taxForms.finalized' : 'payroll.taxForms.open',
        };
    }

    private get downloadPathForT4Summary(): string {
        return `${this.auth.company.id}/t4Summaries/${this.t4Summary.id}/download`;
    }

    private get downloadPathForT619(): string {
        return `${this.auth.company.id}/t619s/${this.t4Summary.craFormT619Id}/download`;
    }
}
